import React, { useCallback, useState } from 'react'
import PropTypes from 'prop-types'
import { useQueryClient } from '@tanstack/react-query'
import Card from '../../../../molecules/Card'
import SydLabel from '../../../../commonDesign/SydLabel'
import SydButton from '../../../../commonDesign/Button'
import SydModal from '../../../../commonDesign/SydModal'
import Text from '../../../../atoms/Text'
import { TEXT_VARIANTS } from '../../../../../constants'
import { useFormattingContext } from '../../../../organisms/FormattingProvider/FormattingContext'
import {
  useGetMigrationStatus,
  useGetMigrationUploadUrls,
  useListMigrationTasks,
  useRetryAllErroredTasks,
  useStartMigrationProcessing
} from '../../../../../api/documentVault'
import { QUERY_KEYS } from '../../../../../api/queryKeys'
import { useGridPageState, useGridSortState } from '../../../../organisms/DenaliAnalysis/tables/operational'
import { BatchDetailSkeleton, StatusBadge, SummaryCardGrid } from '../../shared/BatchComponents'
import MigrationTasksTable from './MigrationTasksTable'
import { getBatchStatus } from './migrationUtils'

const MigrationBatchDetail = ({ batchId }) => {
  const queryClient = useQueryClient()
  const { formatter } = useFormattingContext()
  const [startError, setStartError] = useState(null)
  const [showUploadUrls, setShowUploadUrls] = useState(false)
  const [copiedUrl, setCopiedUrl] = useState(null)
  const [taskFilter, setTaskFilter] = useState('all')
  const { pageState: taskPageState, setPageState: setTaskPageState, resetPageState: resetTaskPageState } = useGridPageState(50)
  const { sortState: taskSortState, setSortState: setTaskSortState } = useGridSortState([{ id: 'batchTaskId', desc: false }])

  const { data, isFetching } = useGetMigrationStatus(batchId, {
    refetchInterval: (queryData) => {
      return queryData?.batch?.isProcessing ? 10000 : false
    }
  })

  const batch = data?.batch
  const taskSummary = data?.taskSummary
  const isAwaitingUpload = !batch?.isProcessing && !batch?.lastFinishedDate && !batch?.isError && (!taskSummary || taskSummary.total === 0)
  const actionsDisabled = !batch || batch.isProcessing

  const taskSortField = taskSortState?.[0]?.id ?? 'batchTaskId'
  const taskSortDir = taskSortState?.[0]?.desc ? 'desc' : 'asc'
  const { data: tasksData, isFetching: isTasksFetching } = useListMigrationTasks(
    { batchId, status: taskFilter === 'all' ? undefined : taskFilter, take: taskPageState.pageSize, skip: taskPageState.pageIndex * taskPageState.pageSize, sortField: taskSortField, sortDir: taskSortDir },
    {
      enabled: !!batchId && taskSummary?.total > 0,
      refetchInterval: () => {
        return batch?.isProcessing ? 10000 : false
      }
    }
  )

  const { data: uploadUrlsData } = useGetMigrationUploadUrls(batchId, { enabled: showUploadUrls })

  const retryAll = useRetryAllErroredTasks()
  const startProcessing = useStartMigrationProcessing()

  const handleCopyUrl = useCallback(async (url, label) => {
    try {
      await navigator.clipboard.writeText(url)
      setCopiedUrl(label)
      setTimeout(() => setCopiedUrl(null), 2000)
    } catch {
      setCopiedUrl('error')
      setTimeout(() => setCopiedUrl(null), 2000)
    }
  }, [])

  const handleRetryAll = useCallback(async () => {
    await retryAll.mutateAsync(batchId)
    queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.migrationBatches] })
  }, [batchId, retryAll, queryClient])

  const handleStartProcessing = useCallback(async () => {
    setStartError(null)
    try {
      await startProcessing.mutateAsync(batchId)
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.migrationBatches] })
    } catch (err) {
      setStartError(err?.response?.detail || err?.response?.data?.detail || err?.response?.data?.routeData?.message || err?.message || 'Failed to start processing')
    }
  }, [batchId, startProcessing, queryClient])

  const handleSummaryClick = useCallback((filter) => {
    setTaskFilter(prev => prev === filter ? 'all' : filter)
    resetTaskPageState()
  }, [resetTaskPageState])

  if (isFetching && !data) {
    return <BatchDetailSkeleton metadataCount={3} />
  }

  if (!batch) {
    return <Card><div className='p-1'>Batch not found</div></Card>
  }

  return (
    <div className='flex flex-col gap-sm'>
      <Card>
        <div className='flex flex-col gap-md p-md'>
          <div className='flex flex-row justify-between items-center'>
            <div className='flex flex-row gap-md items-center'>
              <div className='font-weight-bold font-size-xl'>
                Batch #{batchId}
              </div>
              <StatusBadge status={getBatchStatus(batch, taskSummary)} />
            </div>
            <div className='flex flex-row gap-sm'>
              {!batch.lastFinishedDate && (
                <SydButton size='sm' icon='cloudUpload' variant='outline' onClick={() => setShowUploadUrls(true)}>
                  Upload URLs
                </SydButton>
              )}
              {!!batch.isProcessing && (
                <SydButton size='sm' icon='sync' variant='outline' onClick={() => queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.migrationBatches] })}>
                  Refresh
                </SydButton>
              )}
              {!batch?.isProcessing && !!taskSummary?.retryableErrored && (
                <SydButton
                  variant='outline'
                  onClick={handleRetryAll}
                  processing={retryAll.isLoading}
                  disabled={retryAll.isLoading || batch.isProcessing}
                  icon='sync'
                  size='sm'
                >
                  Retry Retryable Errors ({taskSummary.retryableErrored})
                </SydButton>
              )}
              {!actionsDisabled && isAwaitingUpload && (
                <div className='flex flex-col gap-sm p-sm'>
                  <SydButton
                    variant='primary'
                    onClick={handleStartProcessing}
                    processing={startProcessing.isLoading}
                    disabled={startProcessing.isLoading}
                    icon='playArrow'
                  >
                    Start Processing
                  </SydButton>
                  {startError && <div className='font-size-sm' style={{ color: 'var(--color-danger-500)' }}>{startError}</div>}
                </div>
              )}
            </div>
          </div>
          <div style={{ borderTop: '1px solid var(--color-neutral-200)' }} className='pt-3'>
            <div className='flex flex-row gap-xl'>
              <SydLabel label='Type'>
                <Text variant={TEXT_VARIANTS.body1} text='Historical Doc Migration' />
              </SydLabel>
              <SydLabel label='Orion Firm'>
                <Text variant={TEXT_VARIANTS.body1} text={String(batch.metadata?.orionFirmId || '-')} />
              </SydLabel>
              <SydLabel label='Started'>
                <Text variant={TEXT_VARIANTS.body1} text={batch.lastStartedDate ? formatter(batch.lastStartedDate, '&M/D/YY h:mm a') : '-'} />
              </SydLabel>
              {!!batch.lastFinishedDate && (
                <SydLabel label='Completed'>
                  <Text variant={TEXT_VARIANTS.body1} text={formatter(batch.lastFinishedDate, '&M/D/YY h:mm a')} />
                </SydLabel>
              )}
            </div>
          </div>
        </div>
      </Card>

      {taskSummary && (
        <SummaryCardGrid
          taskSummary={taskSummary}
          taskFilter={taskFilter}
          onFilterClick={handleSummaryClick}
        />
      )}

      {taskSummary?.total > 0 && (
        <MigrationTasksTable
          tasks={tasksData?.tasks ?? []}
          total={tasksData?.total ?? 0}
          disabled={!!batch.isProcessing}
          loading={isTasksFetching}
          filter={taskFilter}
          pageState={taskPageState}
          sortState={taskSortState}
          onPageChange={setTaskPageState}
          onSortChange={setTaskSortState}
        />
      )}

      <SydModal
        open={showUploadUrls}
        onClose={() => setShowUploadUrls(false)}
        title='Upload URLs'
        subtitle='Share these presigned PUT URLs with the planning team. New URLs are generated each time (24hr expiry).'
        size='large'
      >
        {uploadUrlsData?.uploadUrls ? (
          <div className='flex flex-col gap-sm'>
            <div className='flex flex-col gap-xs'>
              <div className='font-weight-bold font-size-sm'>Mapping File (mapping.json)</div>
              <div style={{ padding: '8px', fontFamily: 'monospace', fontSize: '0.75rem', border: '1px solid var(--color-neutral-200)', borderRadius: 'var(--border-radius-sm)', backgroundColor: 'var(--color-neutral-050)', wordBreak: 'break-all' }}>
                {uploadUrlsData.uploadUrls.mapping}
              </div>
              <SydButton size='sm' variant='outline' onClick={() => handleCopyUrl(uploadUrlsData.uploadUrls.mapping, 'mapping')}>
                {copiedUrl === 'mapping' ? 'Copied!' : 'Copy Mapping URL'}
              </SydButton>
            </div>
            <div className='flex flex-col gap-xs'>
              <div className='font-weight-bold font-size-sm'>Upload File (upload.zip)</div>
              <div style={{ padding: '8px', fontFamily: 'monospace', fontSize: '0.75rem', border: '1px solid var(--color-neutral-200)', borderRadius: 'var(--border-radius-sm)', backgroundColor: 'var(--color-neutral-050)', wordBreak: 'break-all' }}>
                {uploadUrlsData.uploadUrls.zip}
              </div>
              <SydButton size='sm' variant='outline' onClick={() => handleCopyUrl(uploadUrlsData.uploadUrls.zip, 'zip')}>
                {copiedUrl === 'zip' ? 'Copied!' : 'Copy Zip URL'}
              </SydButton>
            </div>
          </div>
        ) : (
          <div className='font-size-sm' style={{ color: 'var(--color-neutral-400)' }}>Loading URLs...</div>
        )}
      </SydModal>
    </div>
  )
}

MigrationBatchDetail.propTypes = {
  batchId: PropTypes.number.isRequired
}

export default MigrationBatchDetail
